import { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import Button from 'src/components/Button';
import ModalLayoutBase from 'src/components/ModalLayout';
import { MobileModalType, ModalContent, ModalFooter as ModalFooterBase, ModalHeader, ModalHeaderDescriptionRow, ModalHeaderTitle as ModalHeaderTitleBase } from 'src/components/ModalLayout';
import useInterestsBulkMutation from 'src/hooks/useInterestsBulkMutation';
import { closeModalAtom } from 'src/stores/modal/atoms';
import { resetRegisterAtom } from 'src/stores/register/atoms';
import { showCommonErrorToastAtom, showToastAtom } from 'src/stores/toast/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalNavButtonType, ModalType } from 'src/types/Modal';
import { ToastType } from 'src/types/Toast';
import { pushEvent } from 'src/utils/event-gateway';
import { CategorizedInterests } from 'src/utils/interest';
import CategoryChipGroup from './CategoryChipGroup';
const ModalLayout = styled(ModalLayoutBase)`
  width: 500px;
  height: 800px;
`;
const StyledModalHeaderTitle = styled(ModalHeaderTitleBase)`
  font-size: 30px;
  font-weight: 700;
  color: ${({
  theme
}) => theme.color.contentDeep};
  line-height: 40px;

  ${({
  theme
}) => theme.screenSize.mobile} {
    padding: 0px;
  }
`;
const CategorySection = styled.div`
  display: flex;
  flex-direction: column;
`;
const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;
const CategoryIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;
const CategoryName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${({
  theme
}) => theme.color.content12};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;
const CategoryWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  padding: 20px 0;
`;
const ModalFooter = styled(ModalFooterBase)`
  gap: 12px;
`;
type TriggerType = 'SIGN_UP' | 'PROFILE_INPUT';
interface InterestsSelectionProps {
  trigger: TriggerType;
  categorizedInterests: CategorizedInterests[];
  minCount: number;
  maxCount: number;
  onDisplay?: () => void;
  isSkipEnabled?: boolean;
}
const TRANS_KEYS = {
  SIGN_UP: {
    title: 'signup__interests_title',
    desc: 'signup__interests_desc',
    button: {
      no_select: 'signup__interests_button_no_select',
      submit: 'signup__interests_button_next',
      skip: 'signup__interests_button_skip'
    },
    errorToast: 'signup__interests_error_limit_reached'
  },
  PROFILE_INPUT: {
    title: 'profile_update__interests_title',
    desc: 'signup__interests_desc',
    button: {
      no_select: 'signup__interests_button_no_select',
      submit: 'profile_update__interests_button_apply'
    },
    errorToast: 'signup__interests_error_limit_reached'
  }
} as const;
const EVENT_CONFIGS = {
  SIGN_UP: {
    type: EVENT_TYPE.SIGN_UP,
    tab: 'signup',
    page: 'signup_interests',
    impression: {
      name: EVENT_NAME.SIGNUP__IMPRESSION_SIGNUP_INTERESTS,
      target: 'signup_interests'
    },
    click: {
      name: EVENT_NAME.SIGNUP_INTERESTS__CLICK_NEXT,
      target: 'next'
    },
    skip: {
      name: EVENT_NAME.SIGNUP_INTERESTS__CLICK_SKIP,
      target: 'skip'
    }
  },
  PROFILE_INPUT: {
    type: EVENT_TYPE.ONBOARDING,
    tab: 'initial',
    page: 'initial_interests',
    impression: {
      name: EVENT_NAME.INITIAL_INTERESTS__IMPRESSION_INTERESTS,
      target: 'interests'
    },
    click: {
      name: EVENT_NAME.INITIAL_INTERESTS__CLICK_APPLY,
      target: 'apply'
    },
    success: {
      name: EVENT_NAME.INITIAL_INTERESTS__APPLY_INTEREST,
      target: 'interest'
    }
  }
} as const;
const InterestsSelectionModal = ({
  trigger,
  categorizedInterests,
  minCount,
  maxCount,
  onDisplay,
  isSkipEnabled = false
}: InterestsSelectionProps) => {
  const resetRegister = useSetAtom(resetRegisterAtom);
  const closeModal = useSetAtom(closeModalAtom);
  const showToast = useSetAtom(showToastAtom);
  const showCommonErrorToast = useSetAtom(showCommonErrorToastAtom);
  const {
    t
  } = useTranslation();
  const eventConfig = EVENT_CONFIGS[trigger];
  const transKeys = TRANS_KEYS[trigger];
  useEffect(() => {
    onDisplay?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    pushEvent({
      eventType: eventConfig.type,
      eventName: eventConfig.impression.name,
      eventParams: {
        action_category: 'impression',
        tab: eventConfig.tab,
        page: eventConfig.page,
        target: eventConfig.impression.target
      }
    });
  }, [eventConfig]);
  const [selectedInterests, setSelectedInterests] = useState<Set<string>>(new Set());
  const {
    mutate: submitInterests,
    isPending
  } = useInterestsBulkMutation();
  const getButtonText = () => {
    if (selectedInterests.size < minCount) {
      return t(transKeys.button.no_select, {
        count: minCount
      });
    }
    return t(transKeys.button.submit);
  };
  const toggleInterest = useCallback((interestId: string) => {
    setSelectedInterests(prev => {
      const next = new Set(prev);
      if (next.has(interestId)) {
        next.delete(interestId);
      } else {
        if (next.size >= maxCount) {
          showToast({
            type: ToastType.ERROR,
            message: t(transKeys.errorToast, {
              maxCount
            })
          });
          return prev;
        }
        next.add(interestId);
      }
      return next;
    });
  }, [maxCount, transKeys, showToast, t]);
  const handleCloseModal = useCallback(() => {
    closeModal(ModalType.INTERESTS_SELECTION);
    resetRegister();
  }, [resetRegister, closeModal]);
  const handleSuccess = useCallback(() => {
    const interestIds = Array.from(selectedInterests);
    if ('success' in eventConfig) {
      pushEvent({
        eventType: eventConfig.type,
        eventName: eventConfig.success.name,
        eventParams: {
          action_category: 'action',
          tab: eventConfig.tab,
          page: eventConfig.page,
          target: eventConfig.success.target,
          interest: interestIds,
          interest_count: selectedInterests.size,
          before_interest_count: 0,
          after_interest_count: selectedInterests.size
        }
      });
    }
    handleCloseModal();
  }, [selectedInterests, eventConfig, handleCloseModal]);
  const handleSubmitInterests = useCallback(() => {
    const interestIds = Array.from(selectedInterests);
    pushEvent({
      eventType: eventConfig.type,
      eventName: eventConfig.click.name,
      eventParams: {
        action_category: 'click',
        tab: eventConfig.tab,
        page: eventConfig.page,
        target: eventConfig.click.target,
        interest: interestIds,
        interest_count: selectedInterests.size
      }
    });
    submitInterests({
      interestIds
    }, {
      onSuccess: handleSuccess,
      onError: showCommonErrorToast
    });
  }, [selectedInterests, eventConfig, submitInterests, handleSuccess, showCommonErrorToast]);
  const handleSkipInterests = useCallback(() => {
    if (!('skip' in eventConfig)) return;
    pushEvent({
      eventType: eventConfig.type,
      eventName: eventConfig.skip.name,
      eventParams: {
        action_category: 'click',
        tab: eventConfig.tab,
        page: eventConfig.page,
        target: eventConfig.skip.target
      }
    });
    handleCloseModal();
  }, [eventConfig, handleCloseModal]);
  return <ModalLayout isDim closeWhenClickOutside={false} mobileNavButtonType={ModalNavButtonType.NONE} data-testid='InterestsSelectionModal' data-sentry-element="ModalLayout" data-sentry-component="InterestsSelectionModal" data-sentry-source-file="index.tsx">
      <ModalHeader data-sentry-element="ModalHeader" data-sentry-source-file="index.tsx">
        <StyledModalHeaderTitle mobileType={MobileModalType.SCREEN_MODAL} buttonType={ModalNavButtonType.NONE} data-sentry-element="StyledModalHeaderTitle" data-sentry-source-file="index.tsx">
          {t(transKeys.title)}
        </StyledModalHeaderTitle>
        <ModalHeaderDescriptionRow data-sentry-element="ModalHeaderDescriptionRow" data-sentry-source-file="index.tsx">{t(transKeys.desc)}</ModalHeaderDescriptionRow>
      </ModalHeader>
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="index.tsx">
        <CategoryWrap data-sentry-element="CategoryWrap" data-sentry-source-file="index.tsx">
          {categorizedInterests.map(({
          category,
          interests
        }) => <CategorySection key={category.id}>
              <CategoryHeader>
                <CategoryIcon src={category.iconUrl} alt={category.name} />
                <CategoryName>{category.name}</CategoryName>
              </CategoryHeader>
              <CategoryChipGroup interests={interests} selectedInterests={selectedInterests} onToggle={toggleInterest} />
            </CategorySection>)}
        </CategoryWrap>
      </ModalContent>
      <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="index.tsx">
        <Button id='interests_selection_next' loading={isPending} disabled={selectedInterests.size < minCount} onClick={handleSubmitInterests} data-sentry-element="Button" data-sentry-source-file="index.tsx">
          {getButtonText()}
        </Button>
        {'skip' in transKeys.button && isSkipEnabled && <Button id='interests_selection_skip' variant='text' color='secondary' disabled={isPending} onClick={handleSkipInterests}>
            {t(transKeys.button.skip)}
          </Button>}
      </ModalFooter>
    </ModalLayout>;
};
export default InterestsSelectionModal;